"use client";
import Button from "@/app/pwa/components/Button";
import { ErrorText } from "@/app/pwa/components/form/ErrorText";
import { FloatingLabel } from "@/app/pwa/components/form/Label";
import Logo from "@/assets/branding/logo.svg";
import { Card } from "@/app/pwa/components/Card";;
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import InputText from "@/app/pwa/components/form/InputText";
import { zodResolver } from "@hookform/resolvers/zod";

const signInSchema = z.object({ email: z.string(), password: z.string() });
export type SignInType = z.infer<typeof signInSchema>;

export function SignInForm() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [backendError, setBackendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInType>({
    resolver: zodResolver(signInSchema),
  });

  async function onSubmit({ email, password }: SignInType) {
    setLoading(true);
    const res = await signIn("kicker-login", {
      email,
      password,
      redirect: false,
    });
    if (res && !res.error && res.ok) {
      const callBackUrl = searchParams.get("callbackUrl") ?? "/";
      router.replace(callBackUrl);
      router.refresh();
    } else {
      setLoading(false);
      if (res && res.error) setBackendError(res.error);
    }
  }

  function forgotPassword() {}

  return (
    <div className="text-lg max-w-md mx-auto">
      <div className="space-y-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-center pb-4 lg:pb-10">
            <Logo className="w-[200px] h-[48px]"/>
          </div>
          <Card className="py-4 ">
            <h3 className="text-xl font-compressed font-bold text-center">
              Mit kicker-Account anmelden
            </h3>
            <p className="text-center mt-2">
              Bitte melde dich mit deinem kicker-Account an, um alle Funktionen der Vereinsheim-App zu nutzen.
            </p>
            <div className="relative mt-6">
              <InputText
                className="peer"
                type="email"
                id="email"
                {...register("email")}
                placeholder=" "
              />
              <FloatingLabel size="lg" htmlFor="email">
                E-Mail
              </FloatingLabel>
              {errors.email && (
                <ErrorText className={`pl-1 mt-2 text-base`}>
                  {errors.email.message}
                </ErrorText>
              )}
            </div>
            <div className="relative mt-6">
              <InputText
                className="block peer"
                id="password"
                {...register("password")}
                placeholder=" "
                type="password"
              />
              <FloatingLabel size="lg" htmlFor="passwort">
                Passwort
              </FloatingLabel>
              <Link
                className="button button--link-muted text-base p-0 mt-1"
                onClick={forgotPassword}
                href="https://www.kicker.de/meinkicker/login"
                target="_blank"
              >
                Passwort vergessen?
              </Link>
              {errors.password && (
                <ErrorText className={`pl-1 mt-2 text-base`}>
                  {errors.password.message}
                </ErrorText>
              )}
            </div>
            {backendError && (
              <ErrorText className={`mt-6 text-base text-center`}>
                {backendError}
              </ErrorText>
            )}
            <div className="text-center mt-6">
              <Button
                className="w-full inline-flex justify-center"
                color="primary"
                type="submit"
                loading={loading}
              >
                Mit kicker-Account anmelden
              </Button>
            </div>
          </Card>
        </form>
        <Card>
          <h3 className="text-xl font-compressed font-bold text-center">
            Noch keinen kicker-Account?
          </h3>
          <p className="text-center">
            Erstelle einen kostenlosen kicker-Account.
          </p>
          <Link
            href="https://www.kicker.de/meinkicker/registrieren"
            className="button button--outline-secondary mt-4 inline-flex justify-center w-full"
          >
            Meinen kicker-Account erstellen
          </Link>
        </Card>
      </div>
    </div>
  );
}
